import {
  applyCaptionsLanguage,
  type ApplyCaptionsLanguageResult,
  type CaptionFallbackTemplate,
} from '@src/editor/services/applyCaptionsLanguage';
import {
  type CaptionFontSubstitution,
  correctKaraokeDataFonts,
} from '@src/editor/services/captionFontSubstitution';
import { setCaptionsHidden } from '@src/services/karaoke/captionsVisibility';
import {
  ORIGINAL_LANGUAGE,
  resolveCaptionLanguageKey,
} from '@src/utility/captionLanguage';
import { FONTS } from '@src/utility/general';
import { formatLanguageCaption } from '@src/utility/languageCaption';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { ToastRequest } from '@src/stores/toast';
import type { KaraokeStore } from '@src/stores-v2/KaraokeStore';

/**
 * PLAT-10788 (DS6) — switch the editor's caption language, loading a cached
 * per-language variant when one exists instead of always re-producing.
 *
 * Plain orchestration (NOT a React hook) shared by the two switch surfaces —
 * Creator Studio's `useCaptionsLanguageApplier` (main `rootStore.karaokeStore`)
 * and the clip preview's `ClipTranscriptBridge` (the clip's owned
 * `_karaokeStore`). Each passes its own editor + `KaraokeStore`.
 *
 * Behavior:
 *  - Already on the target language → `noop`.
 *  - **Cache hit** — a cached variant with clips exists (DS6a: an empty
 *    `{ clips: [] }` variant is NOT a hit) → `setActiveLanguage` + load it into
 *    the editor; returns `{ kind: 'applied' }` so callers run the same
 *    post-applied side-effects (refresh + refreshSource + event) as a produce.
 *  - **Cache miss** — `setActiveLanguage` then delegate to `applyCaptionsLanguage`
 *    (the produce primitive); the caller's karaoke refresh writes the produced
 *    variant back into the cache under the target key.
 *
 * A cached variant may have been produced before later transcript edits made
 * while another language was active (reconcile only runs on the live track). So
 * it is loaded with `reconcileAgainstTranscript` (PLAT-10862): the load reconciles
 * it against the current transcript — dropping rows for removed words, adopting
 * rows for restored/added ones — so switching self-heals. (Supersedes the earlier
 * DS6b "no staleness check here".)
 */
export function switchCaptionLanguage(params: {
  editor: IVideoEditor;
  karaokeStore: KaraokeStore;
  /** Raw user pick: a BCP-47 code, or `null`/`'original'` for the source. */
  lang: string | null;
  transcriptLanguage?: string | null;
  originalVideoLanguage?: string | null;
  fallbackTemplate?: CaptionFallbackTemplate;
  /**
   * PLAT-10816 — a user's own pick: one undo entry, and in the clip preview the
   * signal that persists the switch. Inert on a cache hit (no command runs).
   * See `applyCaptionsLanguage`.
   */
  createUndoRedoEntry?: boolean;
  /**
   * PLAT-11416 — resolves the org's curated per-language template variant;
   * consulted only on the produce path (a cached variant is the user's own
   * per-language state and wins). See `applyCaptionsLanguage`.
   */
  resolveLanguageVariant?: (
    parentTemplateId: string,
    languageKey: string,
  ) => CaptionFallbackTemplate | null;
}): ApplyCaptionsLanguageResult {
  const {
    editor,
    karaokeStore,
    lang,
    transcriptLanguage,
    originalVideoLanguage,
    fallbackTemplate,
    createUndoRedoEntry,
    resolveLanguageVariant,
  } = params;

  const config = editor.getDefaultKaraokeConfig();
  if (!config) return { kind: 'no-config' };

  // "Current" is derived from what's actually loaded in the editor
  // (`config.language`), NOT the store's `activeLanguage` — the two can diverge
  // (e.g. on clip mount the config carries the persisted foreign language while
  // `activeLanguage` is not yet set). This matches `applyCaptionsLanguage`'s
  // config-based idempotency so an already-current language is a noop.
  const currentKey = resolveCaptionLanguageKey(
    config.language,
    transcriptLanguage,
    originalVideoLanguage,
  );
  const targetKey = resolveCaptionLanguageKey(
    lang,
    transcriptLanguage,
    originalVideoLanguage,
  );

  if (targetKey === currentKey) {
    return { kind: 'noop', currentLang: config.language || ORIGINAL_LANGUAGE };
  }

  // Read the cached target straight from the map (NOT
  // `getKaraokeDataForLanguage`, which substitutes live editor lines when the
  // key matches `activeLanguage`). DS6a: an empty variant is a miss → produce.
  const cached = karaokeStore.loadedKaraokeDataByLanguage[targetKey];

  if (cached && cached.clips.length > 0) {
    karaokeStore.setActiveLanguage(targetKey);
    // PLAT-10862 — a cached variant is loaded verbatim, but the transcript may
    // have changed since it was produced (edits made while another language was
    // active never reached it). Reconcile it against the current transcript on
    // load so switching self-heals; idempotent when the variant is already fresh.
    // PLAT-11407 — and correct the FONT for the language being switched TO.
    // This used to lean on "hydrate already corrected the map", which holds
    // only when the map has an entry for this language AND hydrate ran after
    // the language was known. Neither is guaranteed: a clip carries no caption
    // data of its own and inherits the story's `original` variant, so the
    // entry loaded here can be one whose font was chosen for the SOURCE
    // script while the captions on screen are translated text — Anton +
    // Japanese is tofu. Correcting at the point the variant reaches the editor
    // is ordering-independent; it is idempotent and still skips brand fonts.
    const fontSafe = correctKaraokeDataFonts(cached, targetKey, {
      knownFamilies: FONTS,
    });
    const hadTrack = editor.hasKaraokeTrack();
    editor.loadKaraokeData(fontSafe, { reconcileAgainstTranscript: true });
    // PLAT-11689 — captions on/off belongs to the video, not the language: a
    // cached variant carries its own configs, so re-apply the current state.
    // Every config, not just the default: the helper skips the ones that
    // already match, so a cache hit that changes nothing runs no command.
    // With no track (a Clear), `hidden` is a leftover: the variant shows.
    setCaptionsHidden(editor, hadTrack && config.hidden === true, {
      createUndoRedoEntry: false,
    });
    // Return currentLang/nextLang in the SAME form `applyCaptionsLanguage` uses
    // (editor `config.language` / the requested `lang`), NOT the canonical keys
    // (`currentKey`/`targetKey`). Callers run `isOriginalLang(nextLang)` on the
    // result (e.g. the applier's caption-language event); returning the source
    // key `en-US` there would read as a translation and mis-fire the event
    // (`?lang=en-US` instead of clearing to original).
    return {
      kind: 'applied',
      currentLang: config.language || ORIGINAL_LANGUAGE,
      nextLang: lang ?? ORIGINAL_LANGUAGE,
    };
  }

  // Miss — produce. Set active first so the caller's post-produce karaoke
  // refresh (`syncFromEditor`) writes the produced variant under `targetKey`.
  karaokeStore.setActiveLanguage(targetKey);
  return applyCaptionsLanguage(editor, lang, fallbackTemplate, {
    createUndoRedoEntry,
    // PLAT-11409 (DS1) — the canonical key, so font substitution also fires
    // when switching back to a non-Latin ORIGINAL language (and restore fires
    // for its Latin one).
    targetLanguageKey: targetKey,
    resolveLanguageVariant,
  });
}

/**
 * PLAT-11409 (DE1 notice) — fire the passive toast for an automatic caption
 * font change. Shared by the Creator Studio applier and the clip preview
 * bridge so both surfaces say the same thing; no-op when the switch didn't
 * touch the font. Lives here (a plain module both already import) rather than
 * in a hook or component file.
 */
export function notifyCaptionFontSubstitution(
  showToast: (toast: ToastRequest) => number,
  result: ApplyCaptionsLanguageResult,
): void {
  if (result.kind !== 'applied') return;
  notifyCaptionFontChange(showToast, result.fontSubstituted);
}

/**
 * The same notice off a bare substitution — for first-produce paths that
 * correct the font without an `ApplyCaptionsLanguageResult` (the CAPTIONS
 * button, PLAT-11674). No-op without a substitution.
 */
export function notifyCaptionFontChange(
  showToast: (toast: ToastRequest) => number,
  substitution: CaptionFontSubstitution | undefined,
): void {
  if (!substitution) return;
  const { fontFamily, languageKey, restored } = substitution;
  // Base subtag only — "Japanese", not "Japanese (Japan)" — a toast is one
  // short line (docs/design-system.md, Notification Toasts).
  const language = formatLanguageCaption(languageKey.split('-')[0]);
  showToast({
    state: 'info',
    message: restored
      ? `Caption font restored to ${fontFamily}`
      : `Caption font changed to ${fontFamily} to support ${language}`,
  });
}
